import React from 'react';
import { LifeBuoy, CircleHelp, PhoneCall, PackageCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/landing/Navbar';
import Footer from '@/components/landing/Footer';
import SEO from '@/components/SEO';
import { SUPPORT_EMAIL, SUPPORT_EMAIL_MAILTO, SUPPORT_PHONE_PRIMARY_DISPLAY, SUPPORT_PHONE_PRIMARY_TEL, TRACKING_URL } from '@/config/links';

const HelpCenter = () => {
  const faqItems = [
    {
      question: 'How can I track my shipment?',
      answer:
        'You can track your shipment in real-time by using the tracking number provided at the time of booking. Visit the tracking page and enter your tracking number to get the latest status.'
    },
    {
      question: 'What are the delivery timelines?',
      answer:
        'Delivery timelines depend on the service level selected and the destination. You can check estimated delivery times during booking or contact our support team for details.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Help Center"
        description="Find answers to common shipping questions, tracking help, support contact details, and shipping guidelines from Boxd Logistics."
        keywords="boxd logistics help center, shipping FAQ, shipment tracking support, ecommerce logistics help"
        canonical="https://boxdlogistics.in/help-center"
        type="website"
        structuredDataId="schema-help-faq"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer
            }
          }))
        }}
      />
      <Navbar />

      <section className="relative pt-32 pb-16 px-4 bg-gradient-to-br from-blue-50 via-white to-blue-50 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px)',
            backgroundSize: '48px 48px'
          }}
        ></div>
        <div className="absolute -top-12 right-0 w-72 h-72 bg-gradient-to-br from-blue-300/20 to-blue-700/10 rounded-full blur-3xl"></div>

        <div className="relative max-w-6xl mx-auto">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-100 text-blue-900 px-4 py-2 text-sm font-semibold mb-6">
            <LifeBuoy className="w-4 h-4" />
            Help Center
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-4">Help Center</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-4xl">
            At Boxd Logistics, we are committed to providing all the support and guidance you need for a seamless shipping experience.
          </p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto space-y-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <CircleHelp className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Frequently Asked Questions (FAQs)</h2>
            </div>
            <div className="space-y-6 text-gray-700">
              {faqItems.map((faq) => (
                <div key={faq.question}>
                  <p className="font-semibold text-gray-900">Q: {faq.question}</p>
                  {faq.question === 'How can I track my shipment?' ? (
                    <p className="mt-1 text-gray-600">
                      A: You can{' '}
                      <Link
                        to={TRACKING_URL}
                        className="font-semibold text-blue-800 hover:text-blue-900"
                      >
                        track your shipment
                      </Link>{' '}
                      in real-time by using the tracking number provided at the time of booking. Visit the tracking
                      page and enter your tracking number to get the latest status.
                    </p>
                  ) : (
                    <p className="mt-1 text-gray-600">A: {faq.answer}</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-blue-50/30 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <PhoneCall className="w-5 h-5 text-blue-800 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Contact Our Support Team</h2>
            </div>
            <p className="text-gray-600 mb-4">
              If your question is not answered in the FAQs, our support team is here to help.
            </p>
            <ul className="space-y-2 text-gray-700">
              <li>
                <strong>Email:</strong>{' '}
                <a href={SUPPORT_EMAIL_MAILTO} className="text-blue-800 hover:text-blue-900">
                  {SUPPORT_EMAIL}
                </a>
              </li>
              <li><strong>Phone:</strong> <a href={`tel:${SUPPORT_PHONE_PRIMARY_TEL}`} className="text-blue-800 hover:text-blue-900">{SUPPORT_PHONE_PRIMARY_DISPLAY}</a></li>
              <li><strong>Live Chat:</strong> Available during business hours on our website.</li>
            </ul>
          </div>

          <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-8 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <PackageCheck className="w-5 h-5 text-blue-900 mt-0.5" />
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Shipping Guidelines</h2>
            </div>
            <ul className="space-y-2 text-gray-700">
              <li>Ensure packages are securely packed and labeled correctly.</li>
              <li>Verify the recipient&apos;s address and contact information.</li>
              <li>Declare the contents accurately to comply with local regulations.</li>
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpCenter;
